import { MenuItem } from 'primeng/api';
import { Role } from '@/core/models/auth.interface';
import { ExtendedMenuItem } from '../menu.types';
import { AI_FEATURE_ENABLED } from '@/shared/utilities/feature-flags';

/**
 * Wealth Manager Menu Configuration
 *
 * Returns the menu items for Wealth Manager role users.
 * Separated from main menu component for better maintainability.
 *
 * @param logoutCallback - Function to call when user clicks logout
 * @param role - Current user's role (WM variants)
 * @param askBettaAlphaCallback - Function to call when user clicks "Ask BettaAlpha"
 * @returns MenuItem object with nested items for wealth manager role
 */
export function getWealthManagerMenuItems(logoutCallback: () => void, role: Role, askBettaAlphaCallback: () => void): MenuItem {
    const isWealthManagerDealsOnly = role === Role.WEALTH_MANAGER_DEALS;
    const canAccessUsers = role === Role.WEALTH_MANAGER_SUPER_USER || role === Role.WEALTH_MANAGER_USERS;
    const canAccessSharedOpportunities = role === Role.WEALTH_MANAGER_SUPER_USER || role === Role.WEALTH_MANAGER_USERS;
    const canAccessTasks = role !== Role.WEALTH_MANAGER_USERS;

    return {
        label: 'Home',
        items: [
            {
                label: 'Dashboard',
                iconPath: '/icons/dashboard.svg',
                routerLink: ['/wm/dashboard']
            },

            ...(AI_FEATURE_ENABLED ? [{
                label: 'Ask BettaAlpha',
                iconPath: '/icons/ai-sparkle.svg',
                command: askBettaAlphaCallback
            } as ExtendedMenuItem] : []),
            {
                label: 'Opportunities',
                iconPath: '/icons/deals.svg',
                items: [
                    {
                        label: 'All Opportunities',
                        routerLink: ['/wm/deal-management/deals']
                    },
                    {
                        label: 'Open',
                        routerLink: ['/wm/deal-management/deals/open']
                    },
                    {
                        label: 'Review',
                        routerLink: ['/wm/deal-management/deals/review']
                    },
                    {
                        label: 'Live',
                        routerLink: ['/wm/deal-management/deals/live']
                    },
                    {
                        label: 'Closing',
                        routerLink: ['/wm/deal-management/deals/closing']
                    },
                    {
                        label: 'Completed',
                        routerLink: ['/wm/deal-management/deals/completed']
                    },
                    {
                        label: 'Declined',
                        routerLink: ['/wm/deal-management/deals/declined']
                    },
                    {
                        label: 'Missed',
                        routerLink: ['/wm/deal-management/deals/missed']
                    }
                ]
            } as ExtendedMenuItem,

            ...(isWealthManagerDealsOnly || !canAccessSharedOpportunities
                ? []
                : [
                      {
                          label: 'Shared Opportunities',
                          iconPath: '/icons/shared.svg',
                          menuType: 'box-submenu',
                          expanded: false,
                          boxSections: [
                              {
                                  title: 'Shared with Relationship Manager',
                                  icon: '/icons/wealth-manager.svg',
                                  items: [
                                      {
                                          label: 'All Opportunities',
                                          routerLink: ['/wm/deal-management/shared-opportunities/rm']
                                      },
                                      {
                                          label: 'Live Opportunities',
                                          routerLink: ['/wm/deal-management/shared-opportunities/rm/live']
                                      },
                                      {
                                          label: 'Closing Opportunities',
                                          routerLink: ['/wm/deal-management/shared-opportunities/rm/closing']
                                      },
                                      {
                                          label: 'Completed Opportunities',
                                          routerLink: ['/wm/deal-management/shared-opportunities/rm/completed']
                                      },
                                      {
                                          label: 'Missed Opportunities',
                                          routerLink: ['/wm/deal-management/shared-opportunities/rm/missed']
                                      }
                                  ]
                              },
                              {
                                  title: 'Shared with Clients',
                                  icon: '/icons/clients.svg',
                                  items: [
                                      {
                                          label: 'All Opportunities',
                                          routerLink: ['/wm/deal-management/shared-opportunities/client']
                                      },
                                      {
                                          label: 'Live Opportunities',
                                          routerLink: ['/wm/deal-management/shared-opportunities/client/live']
                                      },
                                      {
                                          label: 'Expressed Interest',
                                          routerLink: ['/wm/deal-management/shared-opportunities/client/expressed-interest']
                                      },
                                      {
                                          label: 'Closing Opportunities',
                                          routerLink: ['/wm/deal-management/shared-opportunities/client/closing']
                                      },
                                      {
                                          label: 'Completed Opportunities',
                                          routerLink: ['/wm/deal-management/shared-opportunities/client/completed']
                                      },
                                      {
                                          label: 'Declined Opportunities',
                                          routerLink: ['/wm/deal-management/shared-opportunities/client/declined']
                                      },
                                      {
                                          label: 'Missed Opportunities',
                                          routerLink: ['/wm/deal-management/shared-opportunities/client/missed']
                                      }
                                      ,
                                      {
                                          label: 'Withdrawn Opportunities',
                                          routerLink: ['/wm/deal-management/shared-opportunities/client/withdrawn']
                                      }
                                  ]
                              }
                          ]
                      } as ExtendedMenuItem
                  ]),

            ...(isWealthManagerDealsOnly || !canAccessUsers
                ? []
                : [
                      {
                          label: 'Users',
                          iconPath: '/icons/users.svg',
                          items: [
                              ...(role === Role.WEALTH_MANAGER_SUPER_USER
                                  ? [
                                        {
                                            label: 'Wealth Manager Admin',
                                            routerLink: ['/wm/users/wealth-managers']
                                        }
                                    ]
                                  : []),
                              {
                                  label: 'Relationship Managers',
                                  routerLink: ['/wm/users/relationship-managers']
                              },
                              ...(role === Role.WEALTH_MANAGER_SUPER_USER
                                  ? [
                                        {
                                            label: 'Clients',
                                            routerLink: ['/wm/users/clients']
                                        }
                                    ]
                                  : [])
                          ]
                      } as ExtendedMenuItem
                  ]),

            ...(canAccessTasks
                ? [
                      {
                          label: 'Tasks',
                          iconPath: '/icons/book.svg',
                          menuType: 'box-submenu',
                          expanded: false,
                          defaultCollapsed: true,
                          boxSections: [
                              {
                                  title: 'Deals',
                                  items: [
                                      { label: 'Deal Approval', routerLink: ['/wm/new-tasks/deals/deal-approval'] },
                                      // WM: Deal Subscription and Deal Other hidden — WM deal tasks use Deal Approval only.
                                      // { label: 'Deal Subscription', routerLink: ['/wm/new-tasks/deals/deal-subscription'] },
                                      // { label: 'Deal Other', routerLink: ['/wm/new-tasks/deals/deal-other'] },
                                      { label: 'History', routerLink: ['/wm/new-tasks/deals/history'] },
                                  ],
                              },
                              // Users tasks hidden for now across portals.
                              // ...(canAccessUsers
                              //     ? [
                              //           {
                              //               title: 'Users',
                              //               items: [
                              //                   { label: 'Users', routerLink: ['/wm/new-tasks/users/users'] },
                              //                   { label: 'History', routerLink: ['/wm/new-tasks/users/history'] },
                              //               ],
                              //           },
                              //       ]
                              //     : []),
                          ],
                      } as ExtendedMenuItem,
                  ]
                : []),

            {
                label: 'Notifications',
                iconPath: '/icons/bell-outlined.svg',
                routerLink: ['/notifications']
            } as ExtendedMenuItem,
            {
                label: 'Questions',
                iconPath: '/icons/question-answer-file.svg',
                routerLink: ['/questions']
            } as ExtendedMenuItem,
            {
                label: 'Feedback',
                iconPath: '/icons/message.svg',
                routerLink: ['/feedback']
            } as ExtendedMenuItem,
            {
                label: 'Learning Hub',
                iconPath: '/icons/book.svg',
                menuType: 'submenu',
                items: [{ label: 'Content', routerLink: ['/wm/learning-hub'] }],
            } as ExtendedMenuItem,
            {
                label: 'My Profile',
                iconPath: '/icons/user.svg',
                routerLink: ['/user-profile']
            } as ExtendedMenuItem,
            {
                label: 'Logout',
                iconPath: '/icons/log-out.svg',
                command: logoutCallback
            } as ExtendedMenuItem
        ]
    };
}
